import { useEffect, useState, useCallback, useRef } from "react";

import { Link } from "react-router-dom";

import { Settings as Cog, OctagonX, LogOut, Ear, ArrowUpRight, Network, Layers, Sparkles } from "lucide-react";

import { toast } from "sonner";

import { api } from "@/lib/api";

import { useAuth } from "@/context/AuthContext";

import { useLive } from "@/context/LiveContext";

import { useVoice } from "@/hooks/useVoice";
import {useVoiceMeter} from "@/hooks/useVoiceMeter";

import {useOrbPhase} from "@/hooks/useOrbPhase";

import { useChat } from "@/hooks/useChat";

import { Orb, STATE_LABEL } from "@/components/jarvis/Orb";

import { ChatPanel } from "@/components/jarvis/ChatPanel";

import { Composer } from "@/components/jarvis/Composer";

import { DeviceTiles } from "@/components/jarvis/DeviceTiles";

import { AgentCard, CurrentAction, Reminders, stopEverything } from "@/components/jarvis/SidePanels";

import { useToolRunner } from "@/components/jarvis/useToolRunner";

export default function Home() {

  const { logout, user } = useAuth();

  const { refreshTasks, connected, commands } = useLive();

  const firstName=user?.name?.trim().split(/\s+/)[0]||"señor";
  const goToSection=id=>document.getElementById(id)?.scrollIntoView({behavior:window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches?"auto":"smooth",block:"start"});

  const [voiceCfg, setVoiceCfg] = useState({});

  useEffect(() => { api.get("/settings").then((r) => setVoiceCfg(r.data.voice)).catch(() => {}); }, []);

  const chat = useChat();

  const tools = useToolRunner();

  const [viaVoice, setViaVoice] = useState(false);

  const [localMode, setLocalMode] = useState(false);

  useEffect(() => { api.get("/ai/providers").then(({ data }) => setLocalMode(data.current.provider === "google_gemini" && !data.providers.find(p => p.id === "google_gemini")?.key_present)).catch(() => {}); }, []);

  const sendRef = useRef(null);

  const voice = useVoice(voiceCfg, (t) => sendRef.current?.(t, null, true));

  const orbPhase=useOrbPhase(voice.state,commands);
  const meter=useVoiceMeter(voice.micActive,voiceCfg.audio_visualizer!==false);

  const send = useCallback((text, image, fromVoice = false) => {

    setViaVoice(fromVoice);

    chat.send(text, {

      image,

      onStart: (rid) => voice.beginResponse(rid),

      onDone: (full, rid, err) => {

        refreshTasks();

        if (err) { voice.fail(err.message,rid); return; }

        if (voiceCfg.auto_speak !== false || fromVoice) voice.speak(full, rid); else voice.finishSilently(rid);

      },

    });

  }, [chat, voice, voiceCfg, refreshTasks]);

  sendRef.current = send;

  const stop = async () => {

    voice.interrupt();

    chat.stop();

    try {

      const r = await stopEverything();
      window.dispatchEvent(new Event("jarvis:safety-changed"));

      toast.warning("DETENER enviado", { description: `${r.cancelled_commands} órdenes canceladas · ${r.executing_signalled} en ejecución avisadas · ${r.routines_cancel_requested} rutinas` });

    } catch { toast.error("No se pudo contactar al backend para detener"); }

  };

  return (
    <div className={"command-dashboard"+((voice.continuous||chat.messages.length>0)?" is-engaged":"")}>
      <header className="dashboard-topbar">
        <div className="dashboard-brand"><span className="hud-label">CENTRO DE MANDO</span><span className="font-hud" data-testid="app-title">JARVIS<span className="brand-period">.</span></span></div>
        <div className="dashboard-date">{new Date().toLocaleDateString("es-UY", { timeZone: "America/Montevideo", weekday: "long", day: "numeric", month: "long" })}<span>Montevideo, Uruguay</span></div>
        <nav className="dashboard-top-actions" aria-label="Acciones de la cuenta">
          <span className={"dashboard-live "+(connected?"is-online":"")}><i/>{connected?"Canal activo":"Reconectando"}</span>
          <Link to="/redes" className="dashboard-social-link" data-testid="nav-social"><Network size={16}/>Redes<ArrowUpRight size={13}/></Link>
          <Link to="/ajustes" data-testid="nav-settings" className="dashboard-icon" aria-label="Ajustes"><Cog size={19}/></Link>
          <button onClick={logout} data-testid="logout-button" className="dashboard-icon" aria-label="Salir"><LogOut size={18}/></button>
        </nav>
      </header>
      <nav className="dashboard-section-switcher" aria-label="Secciones de Inicio"><button onClick={()=>goToSection("dashboard-assistant")}>Asistente</button><button onClick={()=>goToSection("dashboard-space")}>Mi espacio</button><button onClick={()=>goToSection("dashboard-system")}>Sistema</button></nav>
      <main className="dashboard-grid">
        <aside id="dashboard-system" className="dashboard-context">
          <div className="dashboard-section-label"><span>01 / SISTEMA</span><span className="section-line"/></div>
          <AgentCard/>
          <CurrentAction/>
          <Reminders/>
          <section className="hud-panel dashboard-shortcuts" aria-label="Accesos rápidos"><span className="hud-label">Explorá tu entorno</span>
            <Link to="/hoy"><Layers size={17}/><span>Mi día<small>Tareas, vencimientos y actividad</small></span><ArrowUpRight size={15}/></Link>
            <Link to="/misiones"><Layers size={17}/><span>Misiones<small>Objetivos y progreso persistente</small></span><ArrowUpRight size={15}/></Link>
            <Link to="/ajustes?tab=capacidades"><Sparkles size={17}/><span>Capacidades<small>Estado real de los 200 pedidos</small></span><ArrowUpRight size={15}/></Link>
            <Link to="/redes"><Network size={17}/><span>Redes y cuentas<small>Tu espacio digital</small></span><ArrowUpRight size={15}/></Link>
            <Link to="/ajustes?tab=asistente"><Sparkles size={17}/><span>Tu asistente<small>Preferencias y memoria</small></span><ArrowUpRight size={15}/></Link>
            <Link to="/ajustes?tab=rutinas"><Layers size={17}/><span>Automatizaciones<small>Rutinas a tu medida</small></span><ArrowUpRight size={15}/></Link>
          </section>
        </aside>
        <section id="dashboard-assistant" className="dashboard-core" aria-label="Asistente JARVIS">
          <div className="core-heading"><span className="hud-label">HOLA, {firstName.toLocaleUpperCase("es-UY")}</span><h1>Tu centro de control<span>.</span></h1><p>{voice.continuous?"Una conversación, a tu ritmo.":"Tocá el micrófono una vez y conversemos."}</p></div>
          <div className="reactor-stage"><div className="reactor-aura" aria-hidden="true"/><div className="reactor-reticle" aria-hidden="true"/><span className="reactor-coordinate" aria-hidden="true">J / NEURAL CORE</span><span className="reactor-marker" aria-hidden="true">JARVIS</span>
            <Orb state={orbPhase} audioLevel={voice.micActive&&meter.status==="live"?meter.level:null} size={typeof window!=="undefined"&&window.innerWidth<640?234:300}/>
          </div>
          <div className="reactor-state" data-phase={orbPhase}><i/><span data-testid="voice-state">{STATE_LABEL[orbPhase]}</span>
            {voice.wakeActive&&<span className="wake-state" data-testid="wake-indicator"><Ear size={13}/>Micrófono abierto: esperando «Jarvis»</span>}
          </div>
          {voiceCfg.wake_word&&<button type="button" className="voice-wake-control" onClick={voice.toggleWake} aria-pressed={voice.wakeArmed}>{voice.wakeArmed?"Desactivar palabra Jarvis":"Activar palabra Jarvis en esta sesión"}</button>}
          {voice.micActive&&voiceCfg.audio_visualizer!==false&&<p className="voice-meter-status">{meter.status==="live"?"Orbe siguiendo el volumen del micrófono":meter.status==="unavailable"?"Medición de volumen no disponible; la orbe muestra el estado de voz":"Preparando respuesta visual del micrófono"}</p>}
          {voice.error&&<p className="reactor-error" data-testid="voice-error">{voice.error}</p>}
          <div className="hud-panel conversation-panel">
            <div className="conversation-heading"><span>CONVERSACIÓN</span><span>JARVIS / VOS</span></div>
            {localMode&&<p className="conversation-local">Modo local sin IA: probá «abrí Steam» o «estado de la PC». Para conversar, <Link to="/ajustes?tab=ia">configurá Google</Link>.</p>}
            <ChatPanel messages={chat.messages} onConfirm={tools.askConfirm} interim={voice.state==="escuchando"?voice.interim:""}/>
            <Composer onInterrupt={()=>{chat.stop();voice.interruptAndListen()}} onSend={(t,img)=>send(t,img,false)} voice={voice} busy={chat.busy} showSuggestions={chat.messages.length===0}/>
          </div>
          {viaVoice&&<span className="sr-only">voz</span>}
        </section>
        <aside id="dashboard-space" className="dashboard-devices">
          <div className="dashboard-section-label"><span>02 / TU ESPACIO</span><span className="section-line"/></div>
          <button onClick={stop} data-testid="detener-button" className="dashboard-stop"><OctagonX size={18}/><span>DETENER TODO</span><span className="stop-caption">Interrumpir acciones</span></button>
          <DeviceTiles run={tools.run}/>
        </aside>
      </main>
      {tools.dialog}
    </div>
  );
}
